import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Instagram, Clock, Phone } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import api from "@/api";
import Seo, { breadcrumbSchema, webPageSchema } from "@/components/Seo";

const inputCls = "w-full rounded-xl border border-clayline bg-cream px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:ring-2 focus:ring-terracotta/60";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "contact" });
      toast.success("Message sent. We'll get back over a hot kulhad.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40 pb-24 max-w-7xl mx-auto px-5 md:px-10">
      <Seo
        title="Contact Chai Times | Chai Café Head Office in Bengaluru"
        description="Contact Chai Times for outlet, catering, feedback or franchise enquiries. Head office in HRBR Layout, Bengaluru; email info@chaitimes.in or call +91 70193 68165."
        path="/contact"
        image="/logo.png"
        schema={[
          webPageSchema({
            type: "ContactPage",
            name: "Contact Chai Times",
            description: "Contact the Chai Times head office in Bengaluru for customer, outlet and franchise enquiries.",
            path: "/contact",
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
        ]}
      />
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <SectionTag>Contact</SectionTag>
            <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 leading-[0.98]">
              Have questions?<br /><span className="italic text-terracotta">Let's talk chai.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-12 space-y-6 text-charcoal/70">
              <p className="font-bold text-charcoal">Secander Food &amp; Beverages LLP — Head Office</p>
              <p className="flex gap-3 items-start"><MapPin size={18} className="shrink-0 mt-0.5 text-terracotta" /> No. 415, Ground Floor, 4th B Cross, HRBR Layout, Kalyan Nagar, Bengaluru - 560043</p>
              <p className="flex gap-3 items-center"><Clock size={18} className="text-terracotta" /> 10 AM – 6 PM, Monday to Friday</p>
              <a href="mailto:info@chaitimes.in" data-testid="contact-email-link" className="flex gap-3 items-center hover:text-terracotta transition-colors"><Mail size={18} className="text-terracotta" /> info@chaitimes.in</a>
              <a href="tel:+917019368165" data-testid="contact-phone-link" className="flex gap-3 items-center hover:text-terracotta transition-colors"><Phone size={18} className="text-terracotta" /> +91-70193 68165</a>
              <a href="https://www.instagram.com/chaitimes.in" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex gap-3 items-center hover:text-terracotta transition-colors"><Instagram size={18} className="text-terracotta" /> @chaitimes.in</a>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="border border-clayline rounded-2xl bg-sand/50 p-7 md:p-9" data-testid="contact-form">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta mb-7">Get in touch</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
              </div>
              <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={`${inputCls} mt-4`} data-testid="contact-email-input" />
              <textarea required rows={5} placeholder="Your message…" value={form.message} onChange={set("message")} className={`${inputCls} mt-4`} data-testid="contact-message-input" />
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="mt-6 w-full rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-charcoal text-cream hover:bg-terracotta transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? "Sending…" : "Send Message"} <ArrowRight size={14} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default Contact;
